import React from 'react';

type Block =
  | { type: 'heading'; text: string }
  | { type: 'list'; items: string[] }
  | { type: 'paragraph'; lines: string[] };

/**
 * Minimal markdown parsing covering the shapes used by the legal drafts:
 * #### section headings, "- " bullet lists, **bold**, and paragraphs
 * (consecutive lines join with line breaks). The leading "### n. Title
 * (Draft)" label line is dropped — the sheet/page header already names
 * the doc.
 */
function parseBlocks(markdown: string): Block[] {
  const blocks: Block[] = [];
  let paragraph: string[] = [];
  let list: string[] = [];

  const flushParagraph = () => {
    if (paragraph.length) {
      blocks.push({ type: 'paragraph', lines: paragraph });
      paragraph = [];
    }
  };
  const flushList = () => {
    if (list.length) {
      blocks.push({ type: 'list', items: list });
      list = [];
    }
  };

  for (const raw of markdown.split('\n')) {
    const line = raw.trim();
    if (!line) {
      flushParagraph();
      flushList();
    } else if (line.startsWith('#### ')) {
      flushParagraph();
      flushList();
      blocks.push({ type: 'heading', text: line.slice(5) });
    } else if (line.startsWith('### ')) {
      flushParagraph();
      flushList();
    } else if (line.startsWith('- ')) {
      flushParagraph();
      list.push(line.slice(2));
    } else {
      flushList();
      paragraph.push(line);
    }
  }
  flushParagraph();
  flushList();
  return blocks;
}

function renderInline(text: string): React.ReactNode {
  const parts = text.split(/\*\*(.+?)\*\*/g);
  return parts.map((part, i) =>
    i % 2 === 1 ? (
      <strong key={i} className="font-semibold text-stone-800">
        {part}
      </strong>
    ) : (
      part
    )
  );
}

/** Renders a legal markdown draft in the site's stone-toned typography. */
const LegalContent = ({ markdown }: { markdown: string }) => {
  const blocks = parseBlocks(markdown);
  return (
    <div className="space-y-4 text-sm leading-relaxed text-stone-600">
      {blocks.map((block, i) => {
        if (block.type === 'heading') {
          return (
            <h3 key={i} className="pt-2 text-base font-semibold text-stone-800">
              {block.text}
            </h3>
          );
        }
        if (block.type === 'list') {
          return (
            <ul key={i} className="list-disc space-y-1 pl-5">
              {block.items.map((item, j) => (
                <li key={j}>{renderInline(item)}</li>
              ))}
            </ul>
          );
        }
        return (
          <p key={i}>
            {block.lines.map((line, j) => (
              <React.Fragment key={j}>
                {j > 0 && <br />}
                {renderInline(line)}
              </React.Fragment>
            ))}
          </p>
        );
      })}
    </div>
  );
};

export default LegalContent;
